import { useState, useEffect } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Search, Plus, Mail, Phone, Calendar, Building2, TrendingUp, Users, FileText } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface ReferralLead {
  id: string
  first_name: string
  last_name: string | null
  email: string
  phone: string | null
  message: string | null
  status: string
  created_at: string
  tenant_id: string
  referrer_customer_id: string | null
  tenant?: { name: string }
  referrer?: { full_name: string }
}

interface SystemStats {
  totalLeads: number
  newLeads: number
  convertedLeads: number
  totalTenants: number
  conversionRate: number
}

const SystemCRM = () => {
  const [leads, setLeads] = useState<ReferralLead[]>([])
  const [filteredLeads, setFilteredLeads] = useState<ReferralLead[]>([])
  const [stats, setStats] = useState<SystemStats>({
    totalLeads: 0,
    newLeads: 0,
    convertedLeads: 0,
    totalTenants: 0,
    conversionRate: 0
  })
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedTenant, setSelectedTenant] = useState<string>('all')
  const [tenants, setTenants] = useState<Array<{id: string, name: string}>>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    filterLeads()
  }, [leads, searchTerm, statusFilter, selectedTenant])

  const fetchData = async () => {
    try {
      setLoading(true)

      // Fetch all referral leads with tenant and referrer information
      const { data: leadsData, error: leadsError } = await supabase
        .from('referral_leads')
        .select(`
          *,
          tenants!tenant_id (name),
          customers!referrer_customer_id (full_name)
        `)
        .order('created_at', { ascending: false })

      if (leadsError) throw leadsError

      // Fetch tenants for filter
      const { data: tenantsData, error: tenantsError } = await supabase
        .from('tenants')
        .select('id, name')
        .eq('status', 'active')
        .order('name')

      if (tenantsError) throw tenantsError

      setLeads(leadsData || [])
      setTenants(tenantsData || [])

      // Calculate stats
      const totalLeads = leadsData?.length || 0
      const newLeads = leadsData?.filter(lead => lead.status === 'neue_empfehlung').length || 0
      const convertedLeads = leadsData?.filter(lead => lead.status === 'kunde').length || 0
      const totalTenants = tenantsData?.length || 0
      const conversionRate = totalLeads > 0 ? Math.round((convertedLeads / totalLeads) * 100) : 0

      setStats({
        totalLeads,
        newLeads,
        convertedLeads,
        totalTenants,
        conversionRate
      })

    } catch (error: any) {
      console.error('Error fetching CRM data:', error)
      toast({
        title: "Fehler beim Laden der Daten",
        description: error.message,
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const filterLeads = () => {
    let filtered = leads

    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter)
    }

    if (selectedTenant !== 'all') {
      filtered = filtered.filter(lead => lead.tenant_id === selectedTenant)
    }

    setFilteredLeads(filtered)
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      'neue_empfehlung': 'bg-blue-100 text-blue-800 hover:bg-blue-100',
      'terminiert': 'bg-gray-100 text-gray-800 hover:bg-gray-100',
      'kunde': 'bg-green-100 text-green-800 hover:bg-green-100',
      'zurückgestellt': 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
      'kein_interesse': 'bg-red-100 text-red-800 hover:bg-red-100',
      'offener_vorgang': 'bg-purple-100 text-purple-800 hover:bg-purple-100'
    }
    
    const labels = {
      'neue_empfehlung': 'Neue Empfehlung',
      'terminiert': 'Terminiert',
      'kunde': 'Kunde geworden',
      'zurückgestellt': 'Zurückgestellt',
      'kein_interesse': 'Kein Interesse',
      'offener_vorgang': 'Offener Vorgang'
    }

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.neue_empfehlung}>
        {labels[status as keyof typeof labels] || status}
      </Badge>
    )
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-3xl font-bold tracking-tight">System CRM</h2>
          <p className="text-muted-foreground">
            Zentrale Übersicht aller Empfehlungen im System
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Gesamt Leads
              </CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalLeads}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Neue Leads
              </CardTitle>
              <Plus className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.newLeads}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Konvertiert
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.convertedLeads}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Conversion Rate
              </CardTitle>
              <FileText className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.conversionRate}%</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Aktive Unternehmen
              </CardTitle>
              <Building2 className="h-4 w-4 text-indigo-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalTenants}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="leads" className="space-y-4">
          <TabsList>
            <TabsTrigger value="leads">Alle Leads</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="leads" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Suchen nach Name, E-Mail oder Unternehmen..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8"
                      />
                    </div>
                  </div>
                  
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Status filtern" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Status</SelectItem>
                      <SelectItem value="neue_empfehlung">Neue Empfehlung</SelectItem>
                      <SelectItem value="terminiert">Terminiert</SelectItem>
                      <SelectItem value="kunde">Kunde geworden</SelectItem>
                      <SelectItem value="zurückgestellt">Zurückgestellt</SelectItem>
                      <SelectItem value="kein_interesse">Kein Interesse</SelectItem>
                      <SelectItem value="offener_vorgang">Offener Vorgang</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder="Unternehmen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle Unternehmen</SelectItem>
                      {tenants.map((tenant) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Leads Table */}
            <Card>
              <CardHeader>
                <CardTitle>Empfehlungs-Leads ({filteredLeads.length})</CardTitle>
                <CardDescription>
                  Übersicht aller Empfehlungen im System
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Lade Daten...
                  </div>
                ) : filteredLeads.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Leads gefunden
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>E-Mail</TableHead>
                          <TableHead>Unternehmen</TableHead>
                          <TableHead>Empfehler</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Erstellt</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredLeads.map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell className="font-medium">
                              {lead.first_name} {lead.last_name || ''}
                            </TableCell>
                            <TableCell>{lead.email}</TableCell>
                            <TableCell>{lead.tenant?.name || 'N/A'}</TableCell>
                            <TableCell>{lead.referrer?.full_name || 'Direkt'}</TableCell>
                            <TableCell>{getStatusBadge(lead.status)}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(lead.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>System Analytics</CardTitle>
                <CardDescription>
                  Erweiterte Metriken und Trends
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  Analytics Dashboard wird demnächst verfügbar sein
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  )
}

export default SystemCRM